import { BreadcrumbItem, Breadcrumbs } from "@vds/breadcrumbs";
import { Button, ButtonGroup, TextLinkCaret } from "@vds/buttons";
import { Loader } from "@vds/loaders";
import { Body } from "@vds/typography";
import { Formik } from "formik";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { createNewandGetTagEndPoint, patchTagEndpoint } from "../../configuration";
import axiosInstance from "../../global/axiosInterceptor";
import { hasPermission } from "../../global/utility/permissionUtils";
import { editSiteTag, updateSiteLevelTag } from "../../global/utility/Utils";
import { ImageDownloadMedia, deleteMedia } from "../../model/mediaService";
import { createFormTag, createTagInEditMode, getTagHierarchy } from "../../model/reportService";
import { addRole, revokeRole } from "../../model/roleService";
import { notificationTypes } from "../../layouts/NotificationConfig";
import { SITES_ERROR_MESSAGES, SITES_CHANGED_FIELD_LABELS } from "../../global/constants/sitesConstants";
import { deleteSiteRequest, fetchCountryIdsRequest, fetchSitesSuccess, saveFloorImageMapping, showNotificationBanner, siteTagListUpdated, updateSiteTagListItem } from "../../store/actions";
import { ActiveForm } from "./ActiveForm";
import AddUsers from "./AddUsers";
import CancelConfirmationModal from "./CancelConfirmationModal";
import { BasicForm } from "./components/basicForm";
import DeleteSiteConfirmationModal from "./DeleteSiteConfirmationModal";
import FloorPlanAddSite from "./FloorPlanAddSite";
import ReviewPageNew from "./ReviewPageNew";
import SiteBuilder from "./SiteBuilder";
import { trimNameField } from "./sitesUtil";
import { device } from "../../styles/breakpoints";
export const SiteContainer = styled.div`
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 25px;
  width: 100%;
  height: auto;
`;
export const AddSiteContainerWithButtons = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
`;
export const AddSiteHeading = styled.h1`
  color: #000;
  font-feature-settings:
    "clig" off,
    "liga" off;
  font-family: Verizon-NHG-eDS;
  font-size: 32px;
  font-style: normal;
  font-weight: 300;
  line-height: 36px; /* 112.5% */
  letter-spacing: 0.25px;
  width: auto;
  height: auto;
`;

export const SaveAndCancelButtons = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  gap: var(--space-3X, 12px);
  width: auto;
  height: auto;
`;

export const BackAndNextButtonsContainer = styled.div`
  display: flex;
  justify-content: space-between !important;
  align-items: center;
  height: auto;
  margin-top: 15px;
`;

export const ProgressBarContainer = styled.div`
  /* Mobile-first: CSS Grid for perfect equal widths */
  display: grid;
  grid-template-columns: ${(props) =>
    props.mode === "edit"
      ? "repeat(4, minmax(0, 1fr))"
      : "repeat(5, minmax(0, 1fr))"
  };
  gap: 2px;
  width: 100%;
  max-width: ${(props) => (props.mode === "edit" ? "none" : "50rem")};
  padding: 0 4px;
  overflow-x: hidden;

  /* Adjust button margin for mobile */
  & > .site-buttons-section {
    margin-left: ${(props) => (props.mode === "edit" ? "-12px" : "0")};

    @media ${device.tablet} {
      margin-left: ${(props) => (props.mode === "edit" ? "35px" : "0")};
    }
  }

  @media ${device.tablet} {
    /* Switch to flex for edit mode to keep tabs compact */
    display: ${(props) => (props.mode === "edit" ? "flex" : "grid")};
    flex-wrap: ${(props) => (props.mode === "edit" ? "nowrap" : "wrap")};
    align-items: center;
    gap: ${(props) => (props.mode === "edit" ? "40px" : "6px")};
    padding: 0 12px;
    max-width: none;
  }

  @media ${device.desktop} {
    gap: ${(props) => (props.mode === "edit" ? "40px" : "12px")};
    padding: ${(props) => (props.mode === "edit" ? "0" : "0 24px")};
  }
`;

export const ProgressSection = styled.div`
  /* Mobile-first: grid child with vertical layout */
  display: flex;
  flex-direction: column;
  align-items: ${(props) => (props.mode === "edit" ? "center" : "flex-start")};
  gap: 8px;
  min-width: 0;
  cursor: ${(props) => (props.disabled === true ? "not-allowed" : "pointer")};

  /* Tablet+: Make tabs compact (auto width) in edit mode */
  @media ${device.tablet} {
    flex: ${(props) => (props.mode === "edit" ? "0 0 auto" : "1")};
    white-space: nowrap;
  }

  /* Edit mode text container responsive fixes */
  & > div {
    width: 100%;
    text-align: center;

    /* Responsive font sizing: small on mobile, medium on tablet+ */
    font-size: 14px;
    line-height: 18px;

    @media ${device.tablet} {
      font-size: 16px;
      line-height: 20px;
    }
  }
`;

export const TopBar = styled.div`
  /* Mobile-first: consistent progress bar */
  background: ${(props) => {
    if (props.overrideStyle) {
      return props.index === props.currentFormIndex ? "#EE0000" : "#F6F6F6";
    }
    if (props.mode === "edit") {
      return props.index === props.currentFormIndex ? "#EE0000" : "#FFFFFF";
    } else {
      return props.index < props.currentFormIndex ? "#008331" : props.index === props.currentFormIndex ? "#000000" : "#D8DADA";
    }
  }};
  height: 4px;
  width: 100%;
  border-radius: 2px;
`;

export const FrameForDataContainer = styled.div`
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;
  width: auto;
  height: auto;
`;

export const TitleOfContent = styled.h2`
  color: #000;
  font-feature-settings:
    "clig" off,
    "liga" off;
  font-family: Verizon-NHG-eDS;
  font-size: 24px;
  font-style: normal;
  font-weight: 400;
  line-height: 28px;
  height: auto;
  width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const ContentGrayContainer = styled.div`
  /* Mobile base styles */
  display: flex;
  height: 100%;
  max-height: 580px;
  padding: 16px;
  flex-direction: column;
  align-items: flex-start;
  gap: 18px;
  border-radius: 8px;
  background: ${(props) => (props.mode === "edit" ? "#ffffff" : "var(--gray-95, #f6f6f6)")};
  overflow-x: hidden;
  overflow-y: visible;

  /* Original tablet/desktop styles (unchanged from before) */
  @media ${device.tablet} {
    padding: 24px;
  }

  @media ${device.desktop} {
    padding: 32px;
  }
`;
export const StyledButton = styled.button`
  text-decoration: underline;
  background-color: transparent;
  cursor: ${(props) => (props.disabled ? "default" : "pointer")};
  margin-top: -0.5rem;
  color: ${(props) => (props.disabled ? "#D8DADA" : "inherit")};
`;

function EditSite() {
  const usersInfo = useRef([]);
  const hierarchyFetched = useRef(false);
  const hasTagsApiCalled = useRef(false);
  const confirmedSiteUpdate = useRef(null); // set after PATCH API succeeds, used for optimistic Redux update on Finish

  const { activeSession } = useSelector((state) => state.sessionReducer.activeSession);
  const location = useLocation();
  const [currentFormIndex, setCurrentFormIndex] = useState(0);
  const [formValues, setFormValues] = useState({});
  const [isLargeScreen, setIsLargeScreen] = useState(window.innerWidth >= 768);

  useEffect(() => {
    let timeoutId;
    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setIsLargeScreen(window.innerWidth >= 768);
      }, 250);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [storeSelectedData, setStoreSelectedData] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isSiteDuplicateError, setIsSiteDuplicateError] = useState(false);
  const [isSiteDuplicateErrorYes, setIsSiteDuplicateErrorYes] = useState(false);
  const [isSiteDuplicateErrorNo, setIsSiteDuplicateErrorNo] = useState(false);
  const [isCampusDuplicateError, setIsCampusDuplicateError] = useState(false);
  const [hasNewFloorFlag, setHasNewFloorFlag] = useState(false);
  const [hasStateAndCityTempChanges, setHasStateAndCityTempChanges] = useState(false);
  const mode = location.state?.mode || "add";
  const fromCampusDet = location?.state?.fromCampusPage || false;
  const { stateTagName, stateTagId, cityTagName, cityTagId, siteTagId, siteTagName, campusTagId, description, isCampusSite } = location.state?.record || {};
  const [displaySiteName, setDisplaySiteName] = useState(siteTagName);
  const isDataFromUCPage = !!fromCampusDet && mode === "add";
  const { siteTagList, sites: sitesTableRows } = useSelector((state) => state?.sitesReducer);
  const floorImages = useSelector((state) => state?.imagesReducer.floorImages);
  const sitesData = siteTagList || "{}";
  const { campuses, countries, cities, states } = sitesData;
  const [cityDropdownDisplay, setCityDropdownDisplay] = useState(false);
  const [isFloorPlansLoaded, setFloorPlansLoaded] = useState(false);
  const [isAreaSubAreaLoaded, setAreaSubAreaLoaded] = useState(false);
  const [showCancelConfirmationModal, setCancelConfirmationModal] = useState(false);
  const [imageIds, setImageIds] = useState([]);
  const [fromReviewPage, setFromReviewPage] = useState(false);
  let navigate = useNavigate();
  const dispatch = useDispatch();
  const handleSiteDelete = async (value) => {
    const siteId = value?.siteid;
    const siteName = value?.sitename;
    try {
      if (!hasPermission("Site management", "Delete")) {
        showPermissionsBanner();
      } else {
        await dispatch(deleteSiteRequest(siteId));
        dispatch(fetchCountryIdsRequest(undefined, true));
        showDeleteSiteBanner(siteName);
        navigate("/Sites"); // Navigate after deletion
      }
    } catch (error) {
      console.error("An error occurred while deleting the site");
    }
  };

  const [forms, setForms] = useState([
    {
      initialValues: {
        isCampusSite: isDataFromUCPage ? "1" : "0",
        campusNameId: "",
        newCampusName: "",
        siteName: "",
        country: { label: "", value: "" },
        state: { label: "", value: "" },
        city: { label: "", value: "" },
        siteDescription: "",
        siteTagId: siteTagId || ""
      },
      onValidate: (values) => {
        const campuses = sitesData?.campuses;
        values.campusName = values.campusNameId !== "new" ? campuses?.find((campus) => campus.id === values.campusNameId)?.name : values.newCampusName;
        const errors = {};
        if (mode !== "edit" && values?.isCampusSite === "1" && values?.campusNameId?.value === "new" && !values?.newCampusName) {
          errors.newCampusName = "Campus Name is Required";
        } else if (mode !== "edit" && values.isCampusSite === "1" && !values.campusNameId) {
          errors.campusNameId = "Campus Name is Required";
        }
        if (!values?.siteName?.trim()) {
          errors.siteName = "Site name is Required";
        }
        if (!values?.country?.value) errors.country = "Country is Required";
        if (!values?.state?.value) errors.state = "State is Required";
        if (!values?.city?.value) errors.city = "City is Required";
        return errors;
      }
    },
    {
      initialValues: {
        files: [],
        floorNames: []
      },
      onValidate: (values) => {
        const errors = {};
        if (!values?.files?.length) {
          errors.missingFiles = "At least one floor is required";
        } else {
          errors.files = values.files.map((file) => {
            if (!file.floorName || file.floorName.trim() === "") {
              return { floorName: "Floor name is required" };
            }
          });
        }
        return errors;
      }
    },
    {
      initialValues: {
        sites: [{ floorName: "", areas: [{ areaName: "", subAreas: [] }] }]
      },
      onValidate: (values) => {
        const errors = {};
        if (!values.sites || values.sites.length === 0) {
          errors.sites = "At least one site is required";
        } else {
          const sitesErrors = values.sites.map((site, siteIndex) => {
            const siteErrors = {};
            if (!site.floorName || site.floorName.trim() === "") {
              siteErrors.floorName = "Floor name is required";
            }
            if (site.areas && site.areas.length > 0) {
              const areasErrors = site.areas.map((area, areaIndex) => {
                const areaError = {};
                if (!area?.areaName || area?.areaName.trim() === "") {
                  areaError.areaName = "Area name is required";
                }
                return areaError;
              });
              // .filter((areaError) => Object.keys(areaError).length > 0);
              if (areasErrors.length > 0) {
                siteErrors.areas = areasErrors;
              }
            } else {
              siteErrors.noArea = "One area is required per floor";
            }
            return siteErrors;
          });
          if (sitesErrors?.filter((siteError) => !!siteError.noArea || siteError.areas?.filter((areaError) => Object.keys(areaError)?.length))?.length) {
            errors.sites = sitesErrors;
          }
        }
        return errors;
      }
    },
    {
      initialValues: formValues[3]
        ? formValues[3]
        : {
            users: []
          },
      onValidate: (values) => {
        const errors = {};
        return errors;
      }
    }
  ]);
  const sitesTagList = siteTagList;
  const accountId = sessionStorage.getItem("accountId");
  const selectedCountryName = "United States";
  const [isEditable, setIsEditable] = useState(false);
  const [onScreenLoader, setOnScreenLoader] = useState(false);
  const sections = ["Site Information", "Floor Plans", "Site Builder", mode === "edit" ? "Users" : "Add Users"];
  if (mode !== "edit") {
    sections.push("Review");
  }

  //Campus/Site add/update banners
  const showCampusAddedSuccessBanner = (campusName, showBanner) => {
    const notificationInfo = {
      type: `Campus added successfully`,
      subTitle: `${campusName} has been added`,
      priority: "success",
      showNotification: showBanner
    };
    dispatch(showNotificationBanner(notificationInfo));
  };

  const showSiteAddedSuccessBanner = (siteName, showBanner, bannerMessage) => {
    const subTitle = bannerMessage
      ? `The site ${siteName} has been added to your organization ${bannerMessage}. Go to the users' User Details page or to the Site Details page for ${siteName} to add these users to the site.`
      : `${siteName} has been added`;
    const notificationInfo = {
      type: `Site added successfully`,
      subTitle: subTitle,
      priority: bannerMessage && bannerMessage != "" ? "warning" : "success",
      showNotification: showBanner
    };
    dispatch(showNotificationBanner(notificationInfo));
  };

  const showCampusUpdatedSuccessBanner = (campusName, showBanner) => {
    const notificationInfo = {
      type: `Campus updated successfully`,
      subTitle: `${campusName} has been updated`,
      priority: "success",
      showNotification: showBanner
    };
    dispatch(showNotificationBanner(notificationInfo));
  };
  const showSiteUpdatedSuccessBanner = (siteName, showBanner, bannerMessage) => {
    const subTitle = bannerMessage
      ? `The site ${siteName} has been updated ${bannerMessage}. Go to the users' User Details page or to the Site Details page for ${siteName} to add these users to the site.`
      : `${siteName} has been updated`;
    const notificationInfo = {
      type: `Site updated successfully`,
      subTitle: subTitle,
      priority: bannerMessage && bannerMessage != "" ? "warning" : "success",
      showNotification: showBanner
    };
    dispatch(showNotificationBanner(notificationInfo));
  };

  const applyOptimisticSiteCreate = (createFormTagResponse, values) => {
    if (!createFormTagResponse?.length) return;

    // Walk the response tree to find campus, site, and nearest city ancestor
    let siteTag = null;
    let cityTag = null;
    let campusTagFromResponse = null;
    const findTags = (tags, nearestCity = null) => {
      for (const tag of (tags || [])) {
        if (tag.kind === "ts.tag.campus") campusTagFromResponse = tag;
        if (tag.kind === "ts.tag.site") { siteTag = tag; cityTag = nearestCity; return; }
        findTags(tag.nestedtags, tag.kind === "ts.tag.city" ? tag : nearestCity);
        if (siteTag) return;
      }
    };
    findTags(createFormTagResponse);
    if (!siteTag) return;

    const stateName = values?.state?.label;
    const cityName = cityTag?.name || values?.city?.label;
    // For new campus: no city ancestor in response — campus.tagids[0] is the city ID
    const cityTagId = cityTag?.id
      || campusTagFromResponse?.tagids?.[0]
      || cities?.find((c) => c.name === cityName)?.id;
    const stateTagId = cityTag?.tagids?.[0]
      || cities?.find((c) => c.id === cityTagId)?.tagids?.[0]
      || states?.find((s) => s.name === stateName)?.id;

    // Update siteTagList.sites (and campuses if new campus was created)
    const newSiteEntry = {
      id: siteTag.id,
      kind: siteTag.kind,
      name: siteTag.name,
      tagids: siteTag.tagids || [],
      billingaccountid: siteTag.billingaccountid,
    };
    const siteTagListUpdates = { sites: [...(siteTagList?.sites || []), newSiteEntry] };
    if (campusTagFromResponse) {
      siteTagListUpdates.campuses = [...(siteTagList?.campuses || []), {
        id: campusTagFromResponse.id,
        kind: campusTagFromResponse.kind,
        name: campusTagFromResponse.name,
        tagids: campusTagFromResponse.tagids || [],
        billingaccountid: campusTagFromResponse.billingaccountid,
        description: campusTagFromResponse.description || "",
      }];
    }
    dispatch(siteTagListUpdated({ ...siteTagList, ...siteTagListUpdates }));

    const isNewCampus = values?.isCampusSite === "1" && values?.campusNameId?.value === "new" && campusTagFromResponse;
    const isCampusSiteAdd = values?.isCampusSite === "1" && values?.campusNameId?.value && values?.campusNameId?.value !== "new";
    const campusObj = isCampusSiteAdd ? campuses?.find((c) => c.id === values?.campusNameId?.value) : null;

    if (isNewCampus) {
      const newChild = {
        key: siteTag.id,
        siteid: siteTag.id,
        siteTagId: siteTag.id,
        sitename: siteTag.name,
        siteTagName: siteTag.name,
        description: siteTag.description,
        createdon: siteTag.createdon,
        city: cityName,
        states: stateName,
        stateTagName: stateName,
        stateTagId,
        cityTagName: cityName,
        cityTagId,
        buildings: 1,
        campusTagName: campusTagFromResponse.name,
        campusTagId: campusTagFromResponse.id,
        parentCampus: campusTagFromResponse.name,
        isCampus: false,
        isSiteFromCampus: true,
      };
      const newCampusRow = {
        key: campusTagFromResponse.id,
        campusId: campusTagFromResponse.id,
        sitename: campusTagFromResponse.name,
        createdon: campusTagFromResponse.createdon,
        city: cityName,
        states: stateName,
        stateTagId,
        cityTagId,
        description: campusTagFromResponse.description || "",
        buildings: 1,
        isCampus: true,
        children: [newChild],
      };
      dispatch(fetchSitesSuccess([newCampusRow, ...(sitesTableRows || [])]));
      return;
    }

    if (isCampusSiteAdd && campusObj) {
      const newChild = {
        key: siteTag.id,
        siteid: siteTag.id,
        siteTagId: siteTag.id,
        sitename: siteTag.name,
        siteTagName: siteTag.name,
        description: siteTag.description,
        createdon: siteTag.createdon,
        city: cityName,
        states: stateName,
        stateTagName: stateName,
        stateTagId,
        cityTagName: cityName,
        cityTagId,
        buildings: 1,
        campusTagName: campusObj.name,
        campusTagId: campusObj.id,
        parentCampus: campusObj.name,
        isCampus: false,
        isSiteFromCampus: true,
      };
      const updatedTableRows = (sitesTableRows || []).map((row) =>
        row.campusId === campusObj.id
          ? { ...row, buildings: (row.buildings || 0) + 1, children: [...(row.children || []), newChild] }
          : row
      );
      dispatch(fetchSitesSuccess(updatedTableRows));
    } else {
      const newRow = {
        key: siteTag.id,
        siteid: siteTag.id,
        siteTagId: siteTag.id,
        sitename: siteTag.name,
        siteTagName: siteTag.name,
        description: siteTag.description,
        createdon: siteTag.createdon,
        city: cityName,
        states: stateName,
        stateTagName: stateName,
        stateTagId,
        cityTagName: cityName,
        cityTagId,
        buildings: 1,
        isCampus: false,
      };
      const optimisticTableRows = [newRow, ...(sitesTableRows || [])];
      dispatch(fetchSitesSuccess(optimisticTableRows));
    }
  };

  // Optimistically patch Redux with the new site name so the Sites table
  // shows updated data immediately — before the background hierarchy re-fetch completes
  const applyOptimisticSiteUpdate = (newName, newDescription) => {
    if (!siteTagId) return;
    dispatch(updateSiteTagListItem("sites", siteTagId, { name: newName, description: newDescription }));
    const updatedTableRows = sitesTableRows?.map((row) => {
      if (row.siteTagId === siteTagId) return { ...row, sitename: newName };
      if (row.children) {
        return {
          ...row,
          children: row.children.map((child) =>
            child.siteTagId === siteTagId ? { ...child, sitename: newName } : child
          ),
        };
      }
      return row;
    });
    if (updatedTableRows) dispatch(fetchSitesSuccess(updatedTableRows));
  };

  //Navigate to Sites page
  const SitePage = (value, bannerMsg, createResponse, formValues) => {
    const campusNameId = value?.campusNameId;
    const campusName = value?.campusName || location?.state?.record?.sitename;
    const siteName = value?.siteName || location?.state?.record?.sitename;
    const isCreate = mode !== "edit";
    if (mode === "edit" && confirmedSiteUpdate.current) {
      const { name, description } = confirmedSiteUpdate.current;
      applyOptimisticSiteUpdate(name, description);
      confirmedSiteUpdate.current = null;
    }
    if (isCreate && createResponse?.length) {
      applyOptimisticSiteCreate(createResponse, formValues);
    }
    dispatch(fetchCountryIdsRequest(undefined, true, false));
    if (campusNameId?.value === "new") {
      navigate("/Sites", { state: { hierarchyRefreshed: true } });
      showCampusAddedSuccessBanner(value?.newCampusName, true);
    } else {
      if (mode === "edit" && siteName) {
        navigate("/Sites", { state: { hierarchyRefreshed: true } });
        showSiteUpdatedSuccessBanner(siteName, true, bannerMsg);
      } else if (mode === "edit" && campusName) {
        navigate("/Sites", { state: { hierarchyRefreshed: true } });
        showCampusUpdatedSuccessBanner(campusName, true);
      } else {
        navigate("/Sites", { state: { hierarchyRefreshed: true } });
        showSiteAddedSuccessBanner(siteName, true, bannerMsg);
      }
    }
  };

  const handleBreadcrumbSitesClick = (e) => {
    e.preventDefault();
    if (mode === "edit" && confirmedSiteUpdate.current) {
      const { name, description } = confirmedSiteUpdate.current;
      applyOptimisticSiteUpdate(name, description);
      confirmedSiteUpdate.current = null;
    }
    if (hasTagsApiCalled.current) {
      dispatch(fetchCountryIdsRequest(undefined, true));
      navigate("/Sites", { state: { hierarchyRefreshed: true } });
    } else {
      navigate("/Sites");
    }
  };

  const toggleEdit = () => {
    if (!hasPermission("Site management", "Edit")) {
      showPermissionsBanner();
    } else {
      setIsEditable((prev) => !prev);
    }
  };

  useEffect(() => {
    async function fetchHierarchy() {
      if (siteTagId) {
        if (!siteTagList) return; // siteTagList still loading on refresh — wait for next run
        if (hierarchyFetched.current) return; // already fetched — skip re-runs caused by siteTagList dispatch
        hierarchyFetched.current = true;
        setOnScreenLoader(true);
        try {
          // Use Redux store if the login-time hierarchy fetch already populated it (maxdepth: 8).
          // Fall back to the API only when the store is empty (e.g. hard-refresh before saga finishes).
          const isSiteTagListLoaded = siteTagList?.sites?.length > 0;
          let floors;

          if (isSiteTagListLoaded) {
            // Reconstruct nested structure from the flat Redux arrays — no API call needed.
            floors = (siteTagList.floors || [])
              .filter(f => f.tagids?.includes(siteTagId))
              .map(f => ({
                ...f,
                tagids: [siteTagId],
                nestedtags: (siteTagList.areas || [])
                  .filter(a => a.tagids?.includes(f.id))
                  .map(area => ({
                    ...area,
                    nestedtags: (siteTagList.subareas || []).filter(s => s.tagids?.includes(area.id))
                  }))
              }));
          } else {
            // Redux not yet populated — fetch from API and cache into Redux for next time.
            const hierarchyData = await getTagHierarchy({ maxdepth: 3 }, { id: siteTagId }, activeSession);
            floors = hierarchyData.filter((tag) => tag.kind === "ts.tag.floor") || [];
            const tFloors = floors.map((f) => {
              f.tagids = [siteTagId];
              return f;
            });
            const siteTagFloor = {
              ...siteTagList,
              floors: [...tFloors, ...(siteTagList.floors || []).filter((f) => !tFloors.some((tf) => tf.id === f.id))]
            };
            dispatch(siteTagListUpdated(siteTagFloor));
          }

          if (mode === "edit") {
            const initialFiles = await Promise.all(
              floors.map(async (floor) => {
                let matchingImageObj;
                if (floorImages.length) {
                  matchingImageObj = floorImages.find((f) => f.id === floor.id);
                }
                if (!matchingImageObj) {
                  try {
                    const url = await ImageDownloadMedia(floor?.imageid, activeSession);
                    dispatch(saveFloorImageMapping(floor.id, url));
                    return {
                      id: floor?.id || null,
                      name: floor?.name || null,
                      floorName: floor?.name || null,
                      image: floor?.imageid || null,
                      url: url || null
                    };
                  } catch {
                    return {
                      id: floor?.id || null,
                      name: floor?.name || null,
                      floorName: floor?.name || null,
                      image: floor?.imageid || null,
                      url: null
                    };
                  }
                } else {
                  return {
                    id: matchingImageObj?.id || null,
                    name: floor?.name || null,
                    floorName: floor?.name || null,
                    image: floor?.imageid || null,
                    url: matchingImageObj.imageUrl || null
                  };
                }
              })
            );
            const initialFloorNames = floors.map((floor) => floor.name);
            setForms((prevForms) => {
              prevForms[1] = {
                ...prevForms[1],
                initialValues: {
                  ...prevForms[1].initialValues,
                  files: initialFiles,
                  floorNames: initialFloorNames
                }
              };
              prevForms[2] = {
                ...prevForms[2],
                initialValues: {
                  ...prevForms[2].initialValues,
                  sites: floors.map((floor) => ({
                    floorName: floor.name,
                    areas:
                      floor.nestedtags?.map((area) => ({
                        areaName: area.name,
                        id: area.id,
                        subAreas:
                          area.nestedtags?.map((subArea) => ({
                            subAreaName: subArea.name,
                            id: subArea.id
                          })) || []
                      })) || []
                  }))
                }
              };
              return [...prevForms];
            });
            setFloorPlansLoaded(true);
            setAreaSubAreaLoaded(true);
          }
        } catch (error) {
          console.error("Failed to fetch tag hierarchy", error);
        } finally {
          setOnScreenLoader(false);
        }
      }
    }
    fetchHierarchy();
  }, [siteTagId, mode, siteTagList]);

  useEffect(() => {
    if (mode === "edit" && selectedCountryName) {
      setForms((prevForms) => {
        prevForms[0] = {
          ...prevForms[0],
          initialValues: {
            ...prevForms[0].initialValues,
            siteName: siteTagName,
            campusNameId: campusTagId,
            siteDescription: description,
            city: {
              label: cityTagName || "Selecte a City",
              value: ""
            },
            state: {
              label: stateTagName || "Selecte a State",
              value: ""
            },
            country: {
              label: selectedCountryName,
              value: ""
            }
          }
        };
        return [...prevForms];
      });
    } else if (isDataFromUCPage) {
      setForms((prevForms) => {
        prevForms[0] = {
          ...prevForms[0],
          initialValues: {
            ...prevForms[0].initialValues,
            siteName: "",
            campusNameId: location?.state?.record.campusTagId,
            siteDescription: description,
            city: {
              label: location?.state?.record.cityTagName || "Selecte a City",
              value: ""
            },
            state: {
              label: location?.state?.record.stateTagName || "Selecte a State",
              value: ""
            },
            country: {
              label: selectedCountryName,
              value: ""
            }
          }
        };
        return [...prevForms];
      });
    }
  }, [mode, campusTagId, siteTagName, description, selectedCountryName, stateTagName, cityTagName, isDataFromUCPage]);

  const createorUpdateSession = (key, res) => {
    const tagsList = sitesTagList[key];
    const matchingTagIndex = tagsList?.findIndex((tag) => tag?.id === res?.id);
    if (matchingTagIndex >= 0) {
      tagsList[matchingTagIndex] = res;
    } else {
      tagsList?.push(res);
    }
    const updatedSitesTagList = { ...sitesTagList, [key]: tagsList };
    dispatch(siteTagListUpdated(updatedSitesTagList));
  };

  const checkingStateAndCityExits = (stateName, cityName, countryName) => {
    try {
      // const tagData = JSON.parse(sessionStorage.getItem("TagIdsList"));
      const tagData = siteTagList;

      const countryTag = tagData.countries.find((item) => item.name === countryName && item.kind === "ts.tag.country");

      const stateTag = tagData.states.find((item) => item.name === stateName && item.kind === "ts.tag.state" && item.tagids.includes(countryTag?.id));

      const cityTag = tagData.cities.find((item) => item.name === cityName && item.kind === "ts.tag.city" && item.tagids.includes(stateTag?.id));

      return {
        countryExists: !!countryTag,
        stateExists: !!stateTag,
        cityExists: !!cityTag,
        countryId: countryTag ? countryTag.id : null,
        stateId: stateTag ? stateTag.id : null,
        cityId: cityTag ? cityTag.id : null
      };
    } catch (e) {
      console.log("Error fetching country, state, or city tag", e);
      return {
        countryExists: false,
        stateExists: false,
        cityExists: false,
        countryId: null,
        stateId: null,
        cityId: null
      };
    }
  };

  const updateForm1Tags = async (values) => {
    try {
      let updatedSiteTagList = { ...siteTagList };
      let finalPayload = {};
      let newCityCreated = null;
      let newStateCreated = null;
      let locationResolutionFailed = false;

      if (values.siteName !== siteTagName || values.siteDescription !== description) {
        finalPayload.name = trimNameField(values.siteName);
        finalPayload.description = values.siteDescription;
      }

      if (values.state.label !== stateTagName || values.city.label !== cityTagName) {
        const { stateExists, cityExists, countryId, stateId, cityId } = checkingStateAndCityExits(values.state.label, values.city.label, values.country.label);

        if (stateExists && cityExists) {
          finalPayload.tagids = [cityId];
        } else if (stateExists && !cityExists) {
          try {
            const payloadForNewCity = {
              accountname: activeSession.accountId,
              tags: [
                {
                  kind: "ts.tag.city",
                  name: values.city.label,
                  tagids: [stateId],
                  nestedtags: []
                }
              ]
            };

            const response = await createNewCityTag(payloadForNewCity);
            if (response && response.length > 0) {
              const newCity = response[0];
              newCityCreated = newCity;
              finalPayload.tagids = [newCity?.id];
            } else {
              console.error("Failed to create new city tag");
              locationResolutionFailed = true;
            }
          } catch (error) {
            console.error("Error creating new city tag:", error);
            locationResolutionFailed = true;
          }
        } else {
          const payloadForMissing = generatePayloadForMissingTags(values.state.label, values.city.label, countryId);
          try {
            const response = await createFormTag(payloadForMissing);
            if (response && response.length > 0) {
              const newState = response[0];
              if (newState && newState.nestedtags && newState.nestedtags.length > 0) {
                const newCity = newState.nestedtags[0];
                newCityCreated = newCity;
                newStateCreated = newState;
                finalPayload.tagids = [newCity.id];
              } else {
                console.error("Failed to retrieve new city tag from response");
                locationResolutionFailed = true;
              }
            } else {
              console.error("Failed to create tags or retrieve new tags from response", response);
              locationResolutionFailed = true;
            }
          } catch (error) {
            console.error("Error creating tags:", error);
            locationResolutionFailed = true;
          }
        }
      }
      let result;
      let patchFailed = false;
      if (Object.keys(finalPayload).length > 0) {
        try {
          result = await axiosInstance.patch(patchTagEndpoint(), {
            accountname: activeSession.accountId,
            resourceidentifier: { id: siteTagId },
            tag: { ...finalPayload }
          });
        } catch (error) {
          console.error("Error in handleSaveClick PATCH:", error);
          patchFailed = true;
        }
      }

      if (patchFailed || locationResolutionFailed) {
        const userChangedName = values.siteName !== siteTagName;
        const userChangedDescription = (values.siteDescription || "") !== (description || "");
        const userChangedCity = values.state.label !== stateTagName || values.city.label !== cityTagName;
        const changedFields = [
          userChangedName && SITES_CHANGED_FIELD_LABELS.SITE_NAME,
          userChangedDescription && SITES_CHANGED_FIELD_LABELS.SITE_DESCRIPTION,
          userChangedCity && SITES_CHANGED_FIELD_LABELS.SITE_LOCATION
        ].filter(Boolean);
        let failType;
        if (userChangedCity && !userChangedName && !userChangedDescription) {
          failType = notificationTypes[107];
        } else if (userChangedName && !userChangedCity && !userChangedDescription) {
          failType = notificationTypes[106];
        } else if (userChangedDescription && !userChangedName && !userChangedCity) {
          failType = notificationTypes[108];
        } else {
          failType = notificationTypes[109];
        }
        const failSubTitle =
          changedFields.length > 0 ? `Failed to update the ${changedFields.join(", ")}. Please try again.` : SITES_ERROR_MESSAGES.SITE_DETAILS_FALLBACK;
        dispatch(
          showNotificationBanner({
            type: failType,
            subTitle: failSubTitle,
            priority: "critical",
            showNotification: true
          })
        );
        return;
      }

      if (newCityCreated) {
        updatedSiteTagList.cities = updatedSiteTagList.cities ? [...updatedSiteTagList.cities, newCityCreated] : [newCityCreated];
        createorUpdateSession("cities", newCityCreated);
      }
      if (newStateCreated) {
        updatedSiteTagList.states = updatedSiteTagList.states ? [...updatedSiteTagList.states, newStateCreated] : [newStateCreated];
        createorUpdateSession("states", newStateCreated);
      }
      if (result && result.data) {
        createorUpdateSession("sites", result.data);
        // Store API-confirmed values — dispatched to Redux only when user actually navigates back to Sites
        confirmedSiteUpdate.current = { name: result.data.name, description: result.data.description };
        setDisplaySiteName(result.data.name);
        const userChangedName = values.siteName !== siteTagName;
        const userChangedDescription = (values.siteDescription || "") !== (description || "");
        const userChangedCity = values.state.label !== stateTagName || values.city.label !== cityTagName;
        const changedFields = [
          userChangedName && SITES_CHANGED_FIELD_LABELS.SITE_NAME,
          userChangedDescription && SITES_CHANGED_FIELD_LABELS.SITE_DESCRIPTION,
          userChangedCity && SITES_CHANGED_FIELD_LABELS.SITE_LOCATION
        ].filter(Boolean);

        if (changedFields.length > 0) {
          dispatch(
            showNotificationBanner({
              type: notificationTypes[53],
              subTitle: `Successfully updated the ${changedFields.join(", ")}.`,
              priority: "success",
              showNotification: true
            })
          );
        }
      }
      goToNextform();
    } catch (error) {
      console.error("Error in handleSaveClick:", error);
    }
  };

  const generatePayloadForMissingTags = (stateName, cityName, countryId) => {
    const payload = {
      accountname: activeSession.accountId,
      tags: [
        {
          kind: "ts.tag.state",
          name: stateName,
          tagids: [countryId],
          nestedtags: [
            {
              kind: "ts.tag.city",
              name: cityName
            }
          ]
        }
      ]
    };
    return payload;
  };

  const createNewCityTag = async (payload) => {
    try {
      const response = await axiosInstance.post(createNewandGetTagEndPoint(), payload);

      const data = response?.data;
      return data;
    } catch (error) {
      console.error("Error creating new city tag:", error);
      throw error;
    }
  };

  const updateForm2Tags = async (values, setFieldValue) => {
    if (values.files) {
      let floorTagResPromises = [];
      values.files.forEach((f, index) => {
        if (f.id) {
          if (f.name !== sitesData?.floors?.find((floor) => floor.id === f.id)?.name) {
            floorTagResPromises.push({
              file: f,
              fileOriginalIndex: index,
              op: updateSiteLevelTag(f.id, trimNameField(f.name), activeSession, f.imageid)
            });
          }
        } else {
          floorTagResPromises.push({
            file: f,
            fileOriginalIndex: index,
            op: createTagInEditMode(activeSession, "ts.tag.floor", trimNameField(f.name), siteTagId, f.imageid, true)
          });
        }
      });
      if (floorTagResPromises.length) {
        let floorTagResonses = await Promise.all(floorTagResPromises.map((p) => p.op));
        floorTagResonses.forEach((floorTagRes, index) => {
          if (Array.isArray(floorTagRes)) {
            const file = floorTagResPromises[index].file;
            createorUpdateSession("floors", floorTagRes[0]);
            setFieldValue(`files.${floorTagResPromises[index].fileOriginalIndex}.id`, floorTagRes[0].id);
            setFieldValue(`sites.${floorTagResPromises[index].fileOriginalIndex}`, {
              floorName: file.name,
              areas: [],
              fileId: file.imageid
            });
            setTimeout(() => goToNextform(), 300);
          } else if (typeof floorTagRes !== "string" && !Array.isArray(floorTagRes)) {
            createorUpdateSession("floors", floorTagRes);
            setFieldValue(`sites.${floorTagResPromises[index].fileOriginalIndex}`, {
              ...values.sites[floorTagResPromises[index].fileOriginalIndex],
              floorName: floorTagRes.name,
              fileId: floorTagRes.imageid
            });
            goToNextform();
          }
        });
      } else {
        goToNextform();
      }
    } else {
      goToNextform();
    }
  };

  const updateForm3Tags = async (values, setFieldValue) => {
    if (values.sites) {
      let updateSitesPromises = [];
      values.sites.forEach(({ areas }, siteIndex) => {
        if (areas.length) {
          areas.forEach((area, areaIndex) => {
            if (area?.id) {
              if (area.areaName !== sitesData.areas?.find((a) => a?.id === area?.id)?.name) {
                updateSitesPromises.push({
                  name: "areas",
                  siteIndex,
                  areaIndex,
                  area,
                  subAreaIndex: null,
                  op: updateSiteLevelTag(area.id, trimNameField(area.areaName), activeSession)
                });
              }
              if (area.subAreas?.length) {
                area.subAreas.forEach((subArea, subAreaIndex) => {
                  if (subArea.id) {
                    if (subArea.subAreaName !== sitesData.subareas.find((s) => s.id === subArea.id)?.name) {
                      updateSitesPromises.push({
                        name: "subAreas",
                        siteIndex,
                        areaIndex,
                        area,
                        subAreaIndex,
                        op: updateSiteLevelTag(subArea.id, trimNameField(subArea.subAreaName), activeSession)
                      });
                    }
                  } else if(trimNameField(subArea.subAreaName).length > 0) {
                    updateSitesPromises.push({
                      name: "subAreas",
                      siteIndex,
                      areaIndex,
                      area,
                      subAreaIndex,
                      op: createTagInEditMode(activeSession, "ts.tag.subArea", trimNameField(subArea.subAreaName), area.id, null, true)
                    });
                  }
                });
              }
            } else {
              const matchingFloorTag = values?.files[siteIndex];
              const floorTagId = matchingFloorTag?.id;
              updateSitesPromises.push({
                name: "areas",
                siteIndex,
                areaIndex,
                area,
                subAreaIndex: null,
                op: createTagInEditMode(activeSession, "ts.tag.area", trimNameField(area.areaName), floorTagId, null, true)
              });
            }
          });
        }
      });
      const updateSitesRes = await Promise.all(updateSitesPromises.map((p) => p.op));
      if (updateSitesRes.length) {
        const siteIndexesArr = [];
        const newAreasRes = updateSitesRes
          .filter((siteTagRes) => {
            const isNewAreaSite = Array.isArray(siteTagRes) && typeof siteTagRes !== "string" && siteTagRes[0].kind === "ts.tag.area";
            if (isNewAreaSite) {
              const itemIndex = updateSitesPromises.findIndex((p) => p.area.areaName === siteTagRes[0].name);
              if (itemIndex > -1) {
                siteIndexesArr.push(itemIndex);
                return true;
              }
            }
          })
          .map((siteTagRes, siteTagResIndex) => {
            const { siteIndex, areaIndex } = updateSitesPromises[siteIndexesArr[siteTagResIndex]];
            createorUpdateSession(updateSitesPromises[siteIndexesArr[siteTagResIndex]].name, siteTagRes[0]);
            setFieldValue(`sites.${siteIndex}.areas.${areaIndex}.id`, siteTagRes[0].id);
            return {
              ...updateSitesPromises[siteIndexesArr[siteTagResIndex]],
              areaTagRes: siteTagRes[0]
            };
          });
        updateSitesRes
          .filter(
            (siteTagRes, siteTagResIndex) =>
              !(Array.isArray(siteTagRes) && typeof siteTagRes !== "string" && updateSitesPromises[siteTagResIndex].name === "areas")
          )
          .forEach((siteTagRes, siteTagResIndex) => {
            if (!Array.isArray(siteTagRes) && typeof siteTagRes !== "string") {
              const { name, siteIndex, areaIndex, subAreaIndex } = updateSitesPromises[siteTagResIndex];
              if (name === "areas") {
                createorUpdateSession("areas", siteTagRes);
                setFieldValue(`sites.${siteIndex}.areas.${areaIndex}.id`, siteTagRes.id);
              } else {
                createorUpdateSession("subareas", siteTagRes);
                setFieldValue(`sites.${siteIndex}.areas.${areaIndex}.subAreas.${subAreaIndex}.id`, siteTagRes.id);
              }
            } else if (Array.isArray(siteTagRes) && typeof siteTagRes !== "string" && updateSitesPromises[siteTagResIndex].name === "subAreas") {
              createorUpdateSession("subareas", siteTagRes[0]);
              const { siteIndex, areaIndex, subAreaIndex } = updateSitesPromises[siteTagResIndex];
              setFieldValue(`sites.${siteIndex}.areas.${areaIndex}.subAreas.${subAreaIndex}.id`, siteTagRes[0].id);
            }
          });
        if (newAreasRes.length) {
          let updateSubAreasPromises = [];
          newAreasRes.forEach((newAreasTag) => {
            const area = newAreasTag.area;
            if (area?.subAreas?.length) {
              area.subAreas.forEach((subArea, subAreaIndex) => {
                updateSubAreasPromises.push({
                  siteIndex: newAreasTag.siteIndex,
                  areaIndex: newAreasTag.areaIndex,
                  subAreaIndex,
                  op: createTagInEditMode(activeSession, "ts.tag.subArea", subArea.subAreaName, newAreasTag.areaTagRes.id, null, true)
                });
              });
            }
          });
          const subAreaTagResArr = await Promise.all(updateSubAreasPromises.map((p) => p.op));
          subAreaTagResArr.forEach((subAreaTagResponses, subAreaTagResponsesIndex) => {
            if (typeof subAreaTagResponses !== "string") {
              const { siteIndex, areaIndex, subAreaIndex } = updateSubAreasPromises[subAreaTagResponsesIndex];
              createorUpdateSession("subareas", subAreaTagResponses[0]);
              setFieldValue(`sites.${siteIndex}.areas.${areaIndex}.subAreas.${subAreaIndex}.id`, subAreaTagResponses[0].id);
            }
          });
          setTimeout(() => goToNextform(), 300);
        } else {
          setTimeout(() => goToNextform(), 300);
        }
      } else {
        setTimeout(() => goToNextform(), 300);
      }
    } else {
      goToNextform();
    }
  };

  const getCityDropdownDisplay = (CityData) => {
    setCityDropdownDisplay(CityData);
  };

  const onNextHandler = async (validateForm, setTouched, values, setFieldValue, setFieldTouched) => {
    const err = !cityDropdownDisplay && (await validateForm());
    setTouched(err, true);
    let isFormValid = true;
    if ( currentFormIndex === 2) {
      isFormValid = !err["sites"]?.some((site) => {
            return Object.keys(site)?.some((key) => {
              return (key === "noArea" && !!site[key]) || (key === "areas" && site[key]?.some((areaErr) => Object.keys(areaErr).length));
            });
          })
      const hasEmptyAreaName = values.sites.some(s => s.areas.some(d => !trimNameField(d.areaName)))
      const hasEmptySubAreaName = values.sites.some(s => s.areas.some(d => d.subAreas.some(g => !trimNameField(g.subAreaName))))
      if (hasEmptyAreaName || hasEmptySubAreaName) {
        isFormValid = false;
        dispatch(
          showNotificationBanner({
            type: hasEmptyAreaName ? notificationTypes[98] : notificationTypes[99],
            subTitle: hasEmptyAreaName ? "Area name cannot be empty" : "Subarea name cannot be empty",
            priority: "critical",
            showNotification: true,
          })
        );
      }
    }else if(currentFormIndex === 1){
      isFormValid = !err["missingFiles"] && !err["files"]?.some((file) => file && Object.keys(file)?.length);
    }else{
      isFormValid = Object.keys(err).length === 0;
    }
    if (isFormValid) {
      if (mode === "edit") {
        switch (currentFormIndex) {
          case 0:
            await updateForm1Tags(values);
            hasTagsApiCalled.current = true;
            break;
          case 1:
            await updateForm2Tags(values, setFieldValue);
            hasTagsApiCalled.current = true;
            break;
          case 2:
            await updateForm3Tags(values, setFieldValue);
            hasTagsApiCalled.current = true;
            break;
          case 3:
            let bannerMsg = "";
            if (siteTagId) {
              bannerMsg = await associateUsersToSite(values.users, siteTagId);
            }
            SitePage(values, bannerMsg);
            break;
          default:
            break;
        }
      } else if (fromReviewPage && currentFormIndex != 1) {
        setCurrentFormIndex(4);
        setFromReviewPage(false);
      } else {
        goToNextform();
      }
    }
  };

  const onSubmitFromReviewHandler = async (validateForm, setTouched, values, setFieldValue, setFieldTouched) => {
    await onNextHandler(validateForm, setTouched, values, setFieldValue, setFieldTouched);
    const err = await validateForm();
    setTouched(err, true);
  };

  const goToNextform = function () {
    setCurrentFormIndex(currentFormIndex + 1);
  };

  const insertInObj = function (innerObj, nthlevel, objtoInsert) {
    if (!nthlevel) {
      Array.isArray(objtoInsert) ? innerObj.push(...objtoInsert) : innerObj.push(objtoInsert);
    } else {
      const internalObj = !!innerObj.nestedtags[0] ? innerObj.nestedtags[0] : innerObj.nestedtags;
      insertInObj(internalObj, --nthlevel, objtoInsert);
    }
  };

  const insertObjAtNthLevel = function (obj, objtoInsert) {
    let level = getDeepLevel(obj);
    if (level) {
      insertInObj(obj, level, objtoInsert);
    } else {
      obj = objtoInsert;
    }
    return obj;
  };

  const getDeepLevel = function (obj, level = 0) {
    if (!obj?.nestedtags) {
      return level;
    } else if (!obj?.nestedtags.length) {
      return ++level;
    } else if (obj?.nestedtags.length) {
      return getDeepLevel(obj?.nestedtags[0], ++level);
    }
  };

  const clearFormSiteTagList = useCallback(
    (id, tagListName) => {
      sitesTagList[tagListName] = sitesTagList[tagListName].filter((item) => item.id !== id);
      dispatch(siteTagListUpdated(sitesTagList));
    },
    [sitesTagList]
  );

  const generateFirstPayload = (values) => {
    let firstPayload = {},
      matchingCampusTag;
    if (values?.newCampusName) {
      matchingCampusTag = campuses?.find((c) => c.name === values?.newCampusName);
    }
    const matchingCountryTag = countries?.find((c) => c.name === values.country?.label);
    const matchingStateTag = states?.find((s) => s.name === values?.state?.label);
    const matchingCityTag = cities?.find((c) => c.name === values?.city?.label);
    if (!matchingCountryTag) {
      const countryTag = {
        kind: "ts.tag.country",
        name: values?.country?.label,
        nestedtags: []
      };
      firstPayload = insertObjAtNthLevel(firstPayload, countryTag);
    }
    if (!matchingStateTag) {
      const stateTag = {
        kind: "ts.tag.state",
        name: values?.state?.label,
        nestedtags: []
      };
      if (matchingCountryTag) {
        stateTag.tagids = [matchingCountryTag?.id];
      }
      firstPayload = insertObjAtNthLevel(firstPayload, stateTag);
    }
    if (!matchingCityTag) {
      const cityTag = {
        kind: "ts.tag.city",
        name: values?.city?.label,
        nestedtags: []
      };
      if (matchingStateTag) {
        cityTag.tagids = [matchingStateTag?.id];
      }
      firstPayload = insertObjAtNthLevel(firstPayload, cityTag);
    }
    if (!matchingCampusTag && values.newCampusName) {
      const campusTag = {
        kind: "ts.tag.campus",
        name: trimNameField(values?.newCampusName),
        nestedtags: [],
        ...(values?.siteDescription &&
          values?.isCampusSite === "1" &&
          values?.campusNameId === "new" && {
            description: values?.siteDescription
          })
      };
      if (matchingCityTag) {
        campusTag.tagids = [matchingCityTag?.id];
      }
      firstPayload = insertObjAtNthLevel(firstPayload, campusTag);
    }
    const siteTag = {
      kind: "ts.tag.site",
      name: trimNameField(values?.siteName),
      nestedtags: [],
      ...(values?.siteDescription &&
        (values?.isCampusSite === "0" || (values?.isCampusSite === "1" && !(values?.campusNameId === "new"))) && {
          description: values?.siteDescription
        })
    };
    if (matchingCampusTag) {
      siteTag.tagids = [matchingCampusTag?.id];
    } else if (values?.isCampusSite === "1" && values?.campusNameId?.value && values?.campusNameId?.value !== "new") {
      siteTag.tagids = [values.campusNameId.value];
    } else if (values?.isCampusSite === "0" && matchingCityTag) {
      siteTag.tagids = [matchingCityTag?.id];
    }
    firstPayload = insertObjAtNthLevel(firstPayload, siteTag);
    return firstPayload;
  };

  const generateSecondPayload = (values) => {
    return values?.files?.map((_, index) => ({
      kind: "ts.tag.floor",
      name: trimNameField(values?.sites[index].floorName),
      imageid: values?.files[index].imageid,
      nestedtags: []
    }));
  };

  const generateThirdPayload = (values) => {
    const thirdPayload = [];
    thirdPayload.push(
      ...values?.sites?.map((site) =>
        site?.areas.map((area) => ({
          kind: "ts.tag.area",
          name: trimNameField(area?.areaName),
          nestedtags: area?.subAreas.map((subarea) => ({
            kind: "ts.tag.subarea",
            name: trimNameField(subarea?.subAreaName)
          }))
        }))
      )
    );
    return thirdPayload;
  };

  const generatePayload = (values) => {
    const payload = {
      accountname: accountId,
      tags: []
    };
    const firstFormPayload = generateFirstPayload(values);
    const secondFormPayload = generateSecondPayload(values);
    const thirdFormPayload = generateThirdPayload(values);
    let tagPayload = { ...firstFormPayload };
    secondFormPayload.forEach((tag, tagIndex) => {
      if (thirdFormPayload[tagIndex]?.length) {
        tag.nestedtags.push(...thirdFormPayload[tagIndex]);
      }
    });
    tagPayload = insertObjAtNthLevel(tagPayload, secondFormPayload);
    payload.tags.push(tagPayload);
    return payload;
  };

  const associateUsersToSite = async (users, siteTagId) => {
    const failedUsers = [];

    if (mode === "add") {
      const addPromises = users.map(async (user) => {
        const roleId = user.customdata?.roles?.[0]?.id;
        const name = `${user?.firstname} ${user?.lastname}`;
        const res = await addRole(activeSession, user.id, roleId, [siteTagId], {
          fromSites: true
        });
        if (res && res.status >= 400 && res.status <= 510) {
          failedUsers.push(name);
        }
        return res;
      });
      await Promise.all(addPromises);
      if (failedUsers.length > 0) {
        return `, however the following user(s) failed to be added to the site: ${failedUsers.join(", ")}`;
      }
      return "";
    } else if (mode === "edit" && isEditable) {
      const defaultUserIds = usersInfo.current?.map((user) => user.id);
      const userIds = users?.map((user) => user.id);
      const newItems = users.filter((user) => !defaultUserIds.includes(user.id));
      const removeItems = usersInfo.current?.filter((user) => !userIds.includes(user.id));

      const addPromises = newItems.map(async (user) => {
        const roleId = user.customdata?.roles?.[0]?.id;
        const name = `${user?.firstname} ${user?.lastname}`;
        const res = await addRole(activeSession, user.id, roleId, [siteTagId], {
          fromSites: true
        });
        if (res && res.status >= 400 && res.status <= 510) {
          failedUsers.push(name);
        }
      });

      const revokePromises = removeItems.map(async (user) => {
        const roleId = user.customdata?.roles?.[0]?.id;
        const name = `${user?.firstname} ${user?.lastname}`;
        const res = await revokeRole(activeSession, user.id, roleId, [siteTagId], { fromSites: true });
        if (res && res.status >= 400 && res.status <= 510) {
          failedUsers.push(name);
        }
      });

      await Promise.all([...addPromises, ...revokePromises]);

      if (failedUsers.length > 0) {
        return `, however the following user(s) failed to be added to or removed from the site - ${failedUsers.join(", ")}`;
      }
      return "";
    }
  };

  function findInObject(obj, predicate) {
    let id = null;

    function search(current) {
      if (predicate(current)) {
        id = current.id;
        return;
      }
      for (let key in current) {
        if (typeof current[key] === "object" && current[key] !== null) {
          search(current[key]);
          if (id) {
            return;
          }
        }
      }
    }

    search(obj);
    return id;
  }
  function isSite(obj) {
    return obj.kind === "ts.tag.site";
  }

  const handleSubmitButton = async (values, cb) => {
    try {
      setOnScreenLoader(true);
      if (mode === "add") {
        const payload = generatePayload(values);
        const createResponse = await createFormTag(payload);
        let newSiteTagId = findInObject(createResponse, isSite);
        let bannerMsg = "";
        if (newSiteTagId) {
          bannerMsg = await associateUsersToSite(values.users, newSiteTagId);
        }
        cb && cb(bannerMsg, createResponse, values);
      }
    } catch (err) {
      console.log("catch block error: " + err);
    } finally {
      setOnScreenLoader(false);
    }
  };

  const handleCancelClick = (values) => {
    if (values.files?.length) {
      if (mode === "edit" && currentFormIndex === 1) {
        const imageIds = values.files.filter((file) => !file.id).map((file) => file.image || file.imageid);
        setImageIds(imageIds); // setting them to delete if user cancelled
      } else {
        const imageIds = values.files.map((file) => file.image || file.imageid);
        setImageIds(imageIds); // setting them to delete if user cancelled
      }
    }
    setCancelConfirmationModal(true);
  };

  const onCancelConfirmation = async (values, setFieldValue) => {
    if ((mode === "edit" && currentFormIndex === 1) || mode === "add") {
      await Promise.all(imageIds.map((imageId) => deleteMedia(imageId, activeSession)));
    }
    if (mode === "edit") {
      setFieldValue(
        "files",
        values?.files?.filter((f) => f.id)
      );
      setFieldValue("siteName", siteTagName);
      setFieldValue("siteDescription", description);
      setFieldValue("state", { label: stateTagName, value: stateTagId });
      setFieldValue("city", {
        label: cityTagName,
        value: cityTagId
      });
      setCancelConfirmationModal(false);
      toggleEdit();
    } else {
      setCancelConfirmationModal(false);
      navigate("/Sites");
    }
  };

  if (onScreenLoader) {
    return (
      <div className="loader-absolute-container">
        <Loader active={true} fullscreen={false} surface="light"></Loader>
      </div>
    );
  }

  const isDisabled = mode === "edit" && !isEditable;

  const showPermissionsBanner = () => {
    const notificationInfo = {
      type: "No Permission to delete site",
      priority: "critical",
      data: { title: "You do not have permissions!" },
      showNotification: true
    };
    dispatch(showNotificationBanner(notificationInfo));
  };

  const showDeleteSiteBanner = (siteName) => {
    const notificationInfo = {
      type: "Delete Site",
      priority: "success",
      data: { title: `Site ${siteName} deleted successfully.` },
      showNotification: true
    };
    dispatch(showNotificationBanner(notificationInfo));
  };

  const handleValidationState = (isError) => {
    setHasError(isError);
  };

  return (
    <>
      {mode === "edit" ? (
        <>
          <Breadcrumbs surface="light">
            <BreadcrumbItem>
              <Link data-testid="rootLayoutV2_settingsLink" to="/settings" className="breadcrums-item_link">
                Settings
              </Link>
            </BreadcrumbItem>
            <BreadcrumbItem>
              <Link data-testid="rootLayoutV2_sitesLink" to="/Sites" className="breadcrums-item_link" onClick={handleBreadcrumbSitesClick}>
                Sites
              </Link>
            </BreadcrumbItem>
            <BreadcrumbItem>Site Details</BreadcrumbItem>
          </Breadcrumbs>
        </>
      ) : (
        <>
          <Breadcrumbs surface="light">
            <BreadcrumbItem>
              <Link data-testid="rootLayoutV2_settingsLink" to="/settings" className="breadcrums-item_link">
                Settings
              </Link>
            </BreadcrumbItem>
            <BreadcrumbItem>Sites</BreadcrumbItem>
          </Breadcrumbs>
        </>
      )}
      {((mode === "edit" && forms[0].initialValues.country?.label) ||
        // && isFloorPlansLoaded &&
        // isAreaSubAreaLoaded
        mode === "add") && (
        <Formik
          initialValues={forms[currentFormIndex]?.initialValues}
          validate={forms[currentFormIndex]?.onValidate}
          onSubmit={forms[currentFormIndex]?.onSubmit}
        >
          {({ values, validateForm, setTouched, setFieldValue, setFieldTouched }) => {
            return (
              <ActiveForm setFieldValue={setFieldValue} mode={mode} forms={forms}>
                <SiteContainer>
                  <AddSiteContainerWithButtons>
                    {mode === "add" ? <AddSiteHeading>Add Site</AddSiteHeading> : <AddSiteHeading>{displaySiteName}</AddSiteHeading>}
                    <SaveAndCancelButtons>
                      {mode === "add" && (
                        <ButtonGroup
                          childWidth={"100%"}
                          viewport={"desktop"}
                          rowQuantity={{ desktop: 2 }}
                          data={
                            // currentFormIndex !== 4
                            //   ?
                            [
                              {
                                children: "Cancel",
                                size: "small",
                                use: "secondary",
                                width: "auto",
                                "data-testid": "addSite_cancelButton",
                                onClick: () => handleCancelClick(values)
                              }
                            ]
                            // : []
                          }
                          alignment={"center"}
                        />
                      )}
                    </SaveAndCancelButtons>
                  </AddSiteContainerWithButtons>

                  <ProgressBarContainer mode={mode}>
                    {sections.map((section, index) => (
                      <ProgressSection
                        key={index}
                        data-testid="addSite_progressSection"
                        onClick={() => ((mode === "edit" && isEditable) || mode === "add" ? {} : setCurrentFormIndex(index))}
                        mode={mode}
                        disabled={mode === "edit" && isEditable}
                      >
                        {mode === "edit" ? (
                          <>
                            <Body bold={true} size={isLargeScreen ? "medium" : "small"} color={!isDisabled ? "A7A7A7" : "#000000"}>
                              {section}
                            </Body>
                            <TopBar index={index} currentFormIndex={currentFormIndex} mode={mode} />
                          </>
                        ) : (
                          <>
                            <TopBar index={index} currentFormIndex={currentFormIndex} mode={mode} />
                            <div
                              style={{
                                width: "100%",
                                wordBreak: "break-word",
                                hyphens: "auto"
                              }}
                            >
                              <Body size={isLargeScreen ? "medium" : "small"} bold={true}>{`${index + 1}. ${section}`}</Body>
                            </div>
                          </>
                        )}
                      </ProgressSection>
                    ))}
                    {mode === "edit" &&
                      (!isEditable ? (
                        <ButtonGroup
                          className="site-buttons-section"
                          data={[
                            {
                              children: "Edit site",
                              size: "small",
                              use: "primary",
                              width: "100px",
                              "data-testid": "addSite_editSiteButton",
                              onClick: toggleEdit,
                              style: {
                                margin: 0,
                                padding: 0
                              }
                            }
                            // !!! Commenting this code on purpose will be re-using this post MMP1 !!!
                            // Please don't remove this commented code
                            // {
                            //   children: "Delete site",
                            //   size: "small",
                            //   use: "secondary",
                            //   width: "125px",
                            //   onClick: () => {
                            //     if (
                            //       !hasPermission("Site management", "Delete")
                            //     ) {
                            //       showPermissionsBanner();
                            //     } else {
                            //       setShowDeleteConfirmationModal(true);
                            //     }
                            //   },
                            //   style: { margin: 0, padding: 0 },
                            // },
                          ]}
                          alignment="right"
                          childWidth="auto"
                          viewport="desktop"
                        />
                      ) : (
                        <StyledButton
                          disabled={hasNewFloorFlag}
                          data-testid="addSite_cancelEditButton"
                          onClick={() => {
                            handleCancelClick(values);
                            setHasStateAndCityTempChanges(false);
                          }}
                        >
                          Cancel edit
                        </StyledButton>
                      ))}
                  </ProgressBarContainer>

                  <FrameForDataContainer
                    className={
                      mode === "edit"
                        ? "d-flex justify-content-flex-start align-items-flex-start w-100"
                        : "d-flex justify-content-center align-items-center w-100"
                    }
                  >
                    <div
                      style={{
                        width: mode === "edit" ? "100%" : "90%",
                        maxWidth: "850px"
                      }}
                    >
                      {mode === "add" && (
                        <TitleOfContent>
                          {sections[currentFormIndex] === "Floor Plans"
                            ? "Floor Plan(s)"
                            : sections[currentFormIndex] === "Review"
                              ? "Review Site"
                              : sections[currentFormIndex]}
                        </TitleOfContent>
                      )}
                      <ContentGrayContainer mode={mode}>
                        {currentFormIndex === 0 && (
                          <BasicForm
                            isEditable={isEditable}
                            mainCityDropdownDisplay={getCityDropdownDisplay}
                            onValidationChange={handleValidationState}
                            setIsSiteDuplicateErrorYes={setIsSiteDuplicateErrorYes}
                            setIsSiteDuplicateErrorNo={setIsSiteDuplicateErrorNo}
                            setIsCampusDuplicateError={setIsCampusDuplicateError}
                            onFloorChange={() => setHasStateAndCityTempChanges(true)}
                          />
                        )}
                        {currentFormIndex === 1 && (
                          <FloorPlanAddSite
                            isEditable={isEditable}
                            formValues={values}
                            // setFormValues={setFormValues}
                            setForms={setForms}
                            nextFormValues={forms[currentFormIndex + 1]?.initialValues}
                            clearFormSiteTagList={clearFormSiteTagList}
                            onFloorUpload={() => setHasNewFloorFlag(true)}
                            onValidationChange={handleValidationState}
                          />
                        )}
                        {currentFormIndex === 2 && (
                          <SiteBuilder
                            isEditable={isEditable}
                            // setFormValues={setFormValues}
                            clearFormSiteTagList={clearFormSiteTagList}
                            onValidationChange={handleValidationState}
                          />
                        )}
                        {currentFormIndex === 3 && (
                          <AddUsers
                            // currentFormIndex={currentFormIndex}
                            // setCurrentFormIndex={setCurrentFormIndex}
                            setUserInfo={(items) => (usersInfo.current = items)}
                            isEditable={isEditable}
                          />
                        )}
                        {currentFormIndex === 4 && mode !== "edit" && (
                          <ReviewPageNew currentFormIndex={currentFormIndex} setCurrentFormIndex={setCurrentFormIndex} setFromReviewPage={setFromReviewPage} />
                        )}
                      </ContentGrayContainer>
                      {mode !== "edit" || isEditable ? (
                        <BackAndNextButtonsContainer>
                          {currentFormIndex !== 0 && (
                            <TextLinkCaret
                              surface="light"
                              iconPosition="left"
                              disabled={currentFormIndex === 0}
                              data-testid="addSite_backTextCaretLink"
                              onClick={() => {
                                setCurrentFormIndex((i) => Math.max(i - 1, 0));
                              }}
                            >
                              Back
                            </TextLinkCaret>
                          )}
                          <div></div>
                          <Button
                            size="large"
                            use="primary"
                            disabled={hasError || isSiteDuplicateErrorYes || isSiteDuplicateErrorNo || isCampusDuplicateError}
                            data-testid="addSite_nextFinishSave&NextConditionalButton"
                            onClick={async () => {
                              if (currentFormIndex === 4 && mode === "add") {
                                handleSubmitButton(values, (bannerMsg, createResponse, formValues) => SitePage(values, bannerMsg, createResponse, formValues));
                              } else {
                                onNextHandler(validateForm, setTouched, values, setFieldValue, setFieldTouched);
                              }
                            }}
                          >
                            {hasNewFloorFlag || hasStateAndCityTempChanges
                              ? "Save & Next"
                              : fromReviewPage
                                ? currentFormIndex === 1
                                  ? "Next"
                                  : "Submit"
                                : mode === "add"
                                  ? currentFormIndex === 4
                                    ? "Finish"
                                    : "Next"
                                  : currentFormIndex === 3
                                    ? "Finish"
                                    : "Next"}
                          </Button>
                        </BackAndNextButtonsContainer>
                      ) : null}
                    </div>
                  </FrameForDataContainer>
                </SiteContainer>
                <CancelConfirmationModal
                  showModal={showCancelConfirmationModal}
                  setShowModal={setCancelConfirmationModal}
                  title={"Cancel"}
                  onSubmit={() => onCancelConfirmation(values, setFieldValue)}
                />
                <DeleteSiteConfirmationModal
                  showModal={showDeleteConfirmationModal}
                  setShowModal={setShowDeleteConfirmationModal}
                  onSubmit={() => handleSiteDelete(location?.state?.record)}
                />
              </ActiveForm>
            );
          }}
        </Formik>
      )}
    </>
  );
}

export default EditSite;
